import React from "react";

import { FieldProps } from "../../../types/FormTypes";
import Select, { type AsyncOptionsLoader, type SelectOptions, type SelectProps } from "../Select/Select";
import Field from "../Field/Field";
import { useField } from "../Form/Form";

export interface SelectFieldProps
  extends Omit<FieldProps<string | null>, "error">,
    Omit<SelectProps, "value" | "onChange" | "onValueChange" | "name" | "options" | "placeholder" | "disabled" | "fullWidth" | "error"> {
  error?: boolean | string;
  options: SelectOptions | AsyncOptionsLoader;
  placeholder?: string;
  allowArbitrary?: boolean;
  formatOptionLabel?: any;
}

const SelectField = ({
  name,
  label,
  options,
  helperText,
  error,
  required,
  disabled,
  fullWidth = false,
  value: controlledValue,
  onChange,
  onValueChange,
  placeholder,
  allowArbitrary,
  className,
  ...props
}: SelectFieldProps) => {
  const field = useField<string | null>({ name, value: controlledValue, onChange, onValueChange, required });
  const value = typeof field.value === "string" || field.value === null ? field.value : "";
  const errorMessage = typeof error === "string" ? error : undefined;
  const hasError = Boolean(field.error || errorMessage || (typeof error === "boolean" ? error : false));

  return (
    <Field
      className={className}
      name={name}
      fullWidth={fullWidth}
    >
      {label && <Field.Label required={required ?? field.required}>{label}</Field.Label>}
      <Field.Control>
        <Select
          ref={field.ref as any}
          name={name}
          value={value}
          onChange={field.onChange}
          onValueChange={field.onValueChange}
          onBlur={field.onBlur}
          options={options}
          placeholder={placeholder || (label ? `Select ${label}` : "Select...")}
          disabled={disabled}
          fullWidth={fullWidth}
          allowArbitrary={allowArbitrary}
          error={hasError}
          {...props}
        />
      </Field.Control>
      <Field.Message
        error={hasError}
        message={errorMessage ?? helperText}
      />
    </Field>
  );
};

export default SelectField;
